import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import LitLabLayout from "../components/LitLabLayout";
import { Loader2, ArrowLeft, ExternalLink, Printer, FileDown } from "lucide-react";
import { litlabPageGet, litlabPagesList } from "../api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LitLabServicePage — /litlab/services/:slug
 *
 * Renders any page authored/scraped into the `litlab_pages` collection.
 * Faithful to the source layout: images preserve their exact width/height
 * and float-align left/right so surrounding paragraphs wrap around them.
 * Includes print-friendly styling and a "Download PDF" button.
 */
export default function LitLabServicePage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [sibling, setSibling] = useState([]);

  useEffect(() => {
    let live = true;
    setPage(null); setError("");
    litlabPageGet(slug)
      .then((d) => { if (live) setPage(d.page); })
      .catch((e) => {
        if (live) setError(e?.response?.data?.detail || "Page not found");
      });
    litlabPagesList().then((d) => { if (live) setSibling(d.pages || []); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);

  return (
    <LitLabLayout>
      <style>{`
        @media print {
          .litlab-no-print, header, nav, footer, aside, .litlab-service-actions { display: none !important; }
          .litlab-service-page-wrap { padding: 0 !important; background: #fff !important; }
          .litlab-page-content { color: #000 !important; }
          .litlab-serif { color: #000 !important; }
          a { color: #000 !important; text-decoration: underline; }
          img { max-width: 100% !important; page-break-inside: avoid; }
        }
      `}</style>
      <section className="min-h-screen bg-white pt-14 pb-16 litlab-service-page-wrap" data-testid={`litlab-service-page-${slug}`}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-10">
          <article className="min-w-0">
            <Link to="/litlab/services"
                  className="inline-flex items-center gap-1 text-sm text-sky-700 hover:text-sky-900 mb-4 litlab-no-print"
                  data-testid="litlab-service-back">
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
            {error ? (
              <div className="rounded-md ring-1 ring-rose-200 bg-rose-50 text-rose-900 px-4 py-3 text-sm">
                {error}
              </div>
            ) : !page ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.3em] text-sky-700 font-bold">L.I.T. Labs · Service</div>
                <h1 className="litlab-serif text-4xl font-bold text-slate-900 mt-2"
                    data-testid="litlab-service-title">
                  {page.title}
                </h1>
                {page.meta_description && (
                  <p className="text-slate-600 mt-3 max-w-3xl">{page.meta_description}</p>
                )}
                {/* Print + PDF actions */}
                <div className="mt-4 flex flex-wrap items-center gap-2 litlab-service-actions"
                     data-testid="litlab-service-actions">
                  <button onClick={() => window.print()}
                          data-testid="litlab-service-print"
                          className="inline-flex items-center gap-1 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-800 px-3 py-1.5 text-xs font-bold">
                    <Printer className="w-3.5 h-3.5" /> Print page
                  </button>
                  <a href={`${API}/litlab/pages/${slug}/download.pdf`}
                     target="_blank" rel="noreferrer"
                     data-testid="litlab-service-download-pdf"
                     className="inline-flex items-center gap-1 rounded-lg bg-sky-700 hover:bg-sky-800 text-white px-3 py-1.5 text-xs font-bold">
                    <FileDown className="w-3.5 h-3.5" /> Download PDF
                  </a>
                </div>
                <div className="mt-6 litlab-page-content text-slate-800 leading-relaxed">
                  {(page.blocks || []).map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                  {/* Clear floats at the end so the page footer never overlaps */}
                  <div style={{ clear: "both" }} />
                </div>
                {page.source_url && (
                  <a href={page.source_url} target="_blank" rel="noopener noreferrer"
                     className="mt-8 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 litlab-no-print">
                    <ExternalLink className="w-3 h-3" /> Source: litlab.com
                  </a>
                )}
              </>
            )}
          </article>
          <aside className="hidden lg:block litlab-no-print">
            <div className="rounded-xl bg-sky-50 ring-1 ring-sky-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">All services</div>
              <ul className="mt-2 space-y-1.5 text-sm" data-testid="litlab-service-sidebar">
                {sibling
                  .slice()
                  .sort((a, b) => (a.nav_order ?? 999) - (b.nav_order ?? 999))
                  .map((s) => (
                    <li key={s.slug}>
                      <Link to={`/litlab/services/${s.slug}`}
                            className={`block rounded-md px-2 py-1.5 ${s.slug === slug
                              ? "bg-sky-700 text-white font-bold"
                              : "text-slate-800 hover:bg-sky-100"}`}>
                        {s.title}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </LitLabLayout>
  );
}

function Block({ block }) {
  if (!block || !block.type) return null;
  if (block.type === "heading") {
    const level = Math.max(2, Math.min(4, block.level || 2));
    const cls = level === 2 ? "text-2xl mt-8 mb-3 font-bold text-slate-900 litlab-serif"
              : level === 3 ? "text-xl  mt-6 mb-2 font-bold text-slate-900"
              :                "text-lg  mt-5 mb-2 font-bold text-slate-900";
    return level === 2 ? <h2 className={cls}>{block.text}</h2>
         : level === 3 ? <h3 className={cls}>{block.text}</h3>
         :               <h4 className={cls}>{block.text}</h4>;
  }
  if (block.type === "paragraph") {
    return <p className="mb-4 litlab-html" dangerouslySetInnerHTML={{ __html: block.html || "" }} />;
  }
  if (block.type === "list") {
    const items = (block.items || []).map((it, i) => <li key={i}>{it}</li>);
    return block.ordered
      ? <ol className="list-decimal list-outside pl-6 mb-4 space-y-1">{items}</ol>
      : <ul className="list-disc list-outside pl-6 mb-4 space-y-1">{items}</ul>;
  }
  if (block.type === "image") {
    return <ImageBlock block={block} />;
  }
  return null;
}

function ImageBlock({ block }) {
  const { src, alt = "", width, height, align = "center", wrap } = block;
  if (!src) return null;
  // Preserve exact aspect ratio; never scale up. Constrain naturally with
  // a max-width so mobile stays sensible.
  const style = {
    maxWidth: "100%",
    height: "auto",
    ...(width ? { width: Math.min(width, 720) } : {}),
  };
  const wrapStyle = align === "center"
    ? { display: "block", margin: "1.25rem auto", clear: "both" }
    : wrap
      ? {
          float: align, // "left" | "right"
          margin: align === "left" ? "0.25rem 1.25rem 1rem 0"
                                    : "0.25rem 0 1rem 1.25rem",
          maxWidth: "55%",
        }
      : { display: "block", margin: "1.25rem auto", clear: "both" };
  return (
    <img src={src} alt={alt} style={{ ...style, ...wrapStyle }}
         width={width || undefined} height={height || undefined}
         loading="lazy" data-testid="litlab-service-image" />
  );
}
